import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { FormContainer, useForm } from "react-hook-form-mui";
import useSubmitEmpaForm from "../../data-access/useSubmitEmpaForm";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFetchEmpaAnswers } from "../../data-access/useFetchEmpaAnswers";
import { LoadingButton } from "@mui/lab";
import { validationSchema } from "../validationSchema";
import { enqueueSnackbar } from "notistack";
import { Home as HomeIcon } from "@mui/icons-material";
import { useAuth } from "src/shared/context/useAuth";
import { useNavigate } from "react-router-dom";
import InfoMessage from "src/shared/components/InfoMessage";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import CloseIcon from "@mui/icons-material/Close";

interface EmpaFormProps {
  errorMsg?: string;

  previousAnswers?: {
    isEmpa?: boolean;
    q1?: boolean;
    q2?: boolean;
    q3?: boolean;
    q4?: boolean;
    q5?: boolean;
    q6?: boolean;
  };

  showExistingEmpaModal?: boolean;

  onSubmit?: (answers: EmpaAnswers) => void;
}

interface EmpaAnswers {
  empaSelection0: string;
  empaSelection1: string;
  empaSelection2: string;
  empaSelection3: string;
  empaSelection4: string;
  empaSelection5: string;
  empaSelection6: string;
}

function EmpaForm({}: EmpaFormProps) {
  const [isEmpa, setIsEmpa] = useState(false);
  const { t } = useTranslation();
  const schema = validationSchema(t);
  const formContext = useForm({
    resolver: yupResolver(schema),
  });
  const { isLoading: isLoadingEmpaAnswers } = useFetchEmpaAnswers(
    formContext,
    setIsEmpa,
  );
  const { mutate: submitEmpaForm, isLoading: isLoadingSubmit } =
    useSubmitEmpaForm();

  const questions = [
    {
      id: 0,
      title: t(
        "Είστε υπόχρεος ‘’παραγωγός’’ (κατασκευαστής ή εισαγωγέας) για σύμβαση με Συστήματα Εναλλακτικής Διαχείρισης (ΣΕΔ) συσκευασιών και άλλων προϊόντων, υπό την έννοια του Ν 4819/2021 ως ισχύει και κατ’ επέκταση, υπόχρεος εγγραφής στο Εθνικό Μητρώο Παραγωγών Ε.Μ.ΠΑ (παρ. 5, άρ. 11 ν. 4819/21 & παρ. 1, αρ. 2, ΥΑ 181504/16 (ΦΕΚ 2454 Β/9-8-16);",
      ),
      name: "empaSelection0",
    },
    {
      id: 1,
      title: t("1. Υπόχρεοι συσκευασιών"),
      text: (
        <>
          <Typography variant="body1" component="span">
            {t(
              `Είμαι παραγωγός, κατασκευαστής ή εισαγωγέας συσκευασμένων προϊόντων (όλων των προϊόντων που διατίθενται συσκευασμένα) και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά .`,
            )}
          </Typography>
          <Typography
            variant="body1"
            component="span"
            fontWeight="bold"
            display="block"
            sx={{ mt: 1 }}
          >
            {t(`ή / και`)}
          </Typography>
          <Typography variant="body1" component="span">
            {t(
              `Κατασκευάζω ή εισάγω συσκευασίες, οι οποίες προορίζονται να γεμίζονται σε σημείο πώλησης (π.χ. κυπελλάκια, περιέκτες τροφίμων, φιάλες ποτών, πακέτα και περιτυλίγματα), συμπεριλαμβανομένων των σακουλών μεταφοράς και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά.`,
            )}
          </Typography>
        </>
      ),
      name: "empaSelection1",
    },
    {
      id: 2,
      title: t("2. Υπόχρεοι ηλεκτρικών στηλών και συσσωρευτών (ΗΣΣ)"),
      text: (
        <Typography variant="body1">
          {t(
            `Είμαι κατασκευαστής ή εισαγωγέας ηλεκτρικών στηλών ή συσσωρευτών (φορητές μπαταρίες, μπαταρίες αυτοκινήτων, μπαταρίες UPS, βιομηχανικές κλπ) συμπεριλαμβανομένων αυτών που είναι ενσωματωμένες σε συσκευές (κινητά τηλέφωνα, laptop, UPS, κλπ), παιχνίδια και οχήματα (αυτοκίνητα, φορτηγά λεωφορεία , ηλεκτρικά ποδήλατα, κλπ) και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά.`,
          )}
        </Typography>
      ),
      name: "empaSelection2",
    },
    {
      id: 3,
      title: t("3. ‘Υπόχρεοι ηλεκτρικού και ηλεκτρονικού εξοπλισμού (ΗΗΕ)"),
      text: (
        <Typography variant="body1">
          {t(
            `Είμαι κατασκευαστής ή εισαγωγέας ηλεκτρικού και ηλεκτρονικού εξοπλισμού (ΗΗΕ) (ηλεκτρικές συσκευές, οθόνες, λαμπτήρες, φωτοβολταικά, ηλεκτρονικά τσιγάρα, κλπ) και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά`,
          )}
        </Typography>
      ),
      name: "empaSelection3",
    },
    {
      id: 4,
      title: t("4. Υπόχρεοι οχημάτων"),
      text: (
        <Typography variant="body1">
          {t(
            `Είμαι εισαγωγέας καινούριων ή μεταχειρισμένων οχημάτων ( κατηγορίας ταξινόμησης Μ1, Ν1 ) και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά.`,
          )}
        </Typography>
      ),
      name: "empaSelection4",
    },
    {
      id: 5,
      title: t("5. Υπόχρεοι ελαστικών"),
      text: (
        <Typography variant="body1">
          {t(
            `Είμαι κατασκευαστής ή ο εισαγωγέας ελαστικών, είτε αυτά κατασκευάζονται/εισάγονται ως ελαστικά είτε τα ελαστικά αποτελούν μέρος ενός (μεταχειρισμένου ή καινούργιου) οχήματος και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά.`,
          )}
        </Typography>
      ),
      name: "empaSelection5",
    },
    {
      id: 6,
      title: t("6. Υπόχρεοι λιπαντικών"),
      text: (
        <Typography variant="body1">
          {t(
            `Είμαι κατασκευαστής ή εισαγωγέας λιπαντικών ελαίων (οχημάτων ,βιομηχανίας , ναυτιλίας, κτλ) και είμαι ο πρώτος που τα διαθέτω στην ελληνική αγορά.`,
          )}
        </Typography>
      ),
      name: "empaSelection6",
    },
  ];

  return (
    <>
      {isLoadingEmpaAnswers ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <FormContainer
          formContext={formContext}
          onSuccess={(formValues) => {
            const formData = new FormData();

            Object.entries(formValues).forEach(([key, value]) => {
              formData.append(key, value?.toString() ?? "");
            });
            submitEmpaForm(formData);
          }}
          onError={(formError) => {
            Object.entries(formError).forEach(([key, value]) => {
              value?.message
                ? enqueueSnackbar(value?.message, { variant: "error" })
                : null;
            });
          }}
        >
          <Box sx={{ width: "80vw", mx: "auto" }}>
            <Typography variant="h4" gutterBottom>
              {t("Ερωτηματολόγιο ΕΜΠΑ")}
            </Typography>
            {isEmpa && (
              <InfoMessage
                message={t(
                  "Έχετε δηλώσει ήδη ότι είστε επιχείρηση υπόχρεη εγγραφής στο Εθνικό Μητρώο Παραγωγών (ΕΜΠΑ). Στην περίπτωση λανθασμένων στοιχείων στο ερωτηματολόγιο μπορείτε να κάνετε ενημέρωση των στοιχείων σας με επανυποβολή του ερωτηματολογίου.",
                )}
                variant={MessageVariant.Warning}
                boldWord={t("Προσοχή!")}
              />
            )}
            <>
              <Box sx={{ width: "80vw", mx: "auto" }}>
                <Box display="flex" flexDirection="column" gap={4}>
                  <>
                    {questions.map((q) => (
                      <Paper key={q.id} sx={{ padding: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {q.title}
                            </Typography>
                            <Typography variant="body1">{q.text}</Typography>
                          </Box>
                          <FormControl
                            component="fieldset"
                            error={
                              !!formContext.formState.errors[
                                q.name as keyof EmpaAnswers
                              ]
                            }
                          >
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              gap={4}
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                              >
                                <Typography variant="body1" fontWeight="bold">
                                  {t("ΝΑΙ")}
                                </Typography>
                                <input
                                  type="radio"
                                  checked={
                                    formContext.watch(
                                      q.name as keyof EmpaAnswers,
                                    ) === "1"
                                  }
                                  style={{ width: "1rem", height: "1rem" }}
                                  onChange={() =>
                                    formContext.setValue(
                                      q.name as keyof EmpaAnswers,
                                      "1",
                                    )
                                  }
                                />
                              </Box>
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                              >
                                <Typography variant="body1" fontWeight="bold">
                                  {t("ΟΧΙ")}
                                </Typography>
                                <input
                                  type="radio"
                                  checked={
                                    formContext.watch(
                                      q.name as keyof EmpaAnswers,
                                    ) === "0"
                                  }
                                  style={{ width: "1rem", height: "1rem" }}
                                  onChange={() =>
                                    formContext.setValue(
                                      q.name as keyof EmpaAnswers,
                                      "0",
                                    )
                                  }
                                />
                              </Box>
                            </Box>
                            {formContext.formState.errors[
                              q.name as keyof EmpaAnswers
                            ]?.message && (
                              <Typography variant="body2" color="error">
                                {
                                  formContext.formState.errors[
                                    q.name as keyof EmpaAnswers
                                  ]?.message
                                }
                              </Typography>
                            )}
                          </FormControl>
                        </Box>
                      </Paper>
                    ))}
                  </>
                </Box>
              </Box>

              <Box mt={2}>
                <Typography sx={{ mb: 2 }}>
                  <strong>{t("ΝΑΙ")}:</strong>{" "}
                  <Trans
                    i18nKey="Εάν  είστε υπόχρεοι σε τουλάχιστον ένα προϊόν από αυτά που υπόκεινται σε Πρόγραμμα Διευρυμένης Ευθύνης Παραγωγού, καλείστε να συνάψετε σύμβαση με Σύστημα/Συστήματα Εναλλακτικής Διαχείρισης (ΣΕΔ) και να εγγραφείτε στο Εθνικό Μητρώο παραγωγών Ε.Μ.ΠΑ. Σας ενημερώνουμε ότι η υποβολή του ερωτηματολογίου ενέχει ισχύ υπεύθυνης δήλωσης του ν. 1599/1986 και οι απαντήσεις θα αποσταλούν μέσω διασύνδεσης στο Εθνικό Μητρώο ΠΑραγωγών (ΕΜΠΑ)"
                    components={{
                      bold: <strong />,
                    }}
                  />
                </Typography>

                <Typography>
                  <strong>{t("ΟΧΙ")}:</strong>{" "}
                  <Trans
                    i18nKey="Εάν δεν είστε υπόχρεοι, η δήλωσή σας αυτή ενέχει ισχύ υπεύθυνης δήλωσης του ν. 1599/1986, με το παρακάτω περιεχόμενο: «με ατομική μου ευθύνη και γνωρίζοντας τις κυρώσεις, που προβλέπονται από της διατάξεις της παρ. 6 του άρθρου 22 του Ν. 1599/1986, δηλώνω ότι δεν είμαι υπόχρεος παραγωγός (κατασκευαστής ή εισαγωγέας) υπό την έννοια του Ν 4819/2021 ως ισχύει και δεν υποχρεούμαι να συνάψω σύμβαση με συστήματα εναλλακτικής διαχείρισης (ΣΕΔ) συσκευασιών και άλλων προϊόντων (“πλαστικών σακουλών μεταφοράς, “ηλεκτρικών στηλών και συσσωρευτών“ (ΗΣΣ), “ ηλεκτρικού και ηλεκτρονικού εξοπλισμού (ΗΗΕ)“, “οχημάτων (ΟΤΚΖ)“, “ελαστικών“ και “λιπαντικών ελαίων“), ούτε να εγγραφώ στο Εθνικό Μητρώο ΠΑραγωγών Ε.Μ.ΠΑ (παρ. 5, άρ. 11 ν. 4819/21 & παρ. 1, αρ. 2, ΥΑ 181504/16 (ΦΕΚ 2454 Β/9-8-16)»"
                    components={{
                      bold: <strong />,
                    }}
                  />
                </Typography>
              </Box>

              <Box mt={2} ml={4}>
                <Typography sx={{ mb: 2 }}>
                  <Trans
                    i18nKey="Μετά την πρώτη συμπλήρωση του ερωτηματολογίου με κάθε είσοδο του υπόχρεου στο ΓΕΜΗ, θα καλείται η βάση δεδομένων του Ε.Μ.ΠΑ (WS) και όταν κάποιος δεν είναι εγγεγραμμένος στο Ε.Μ.ΠΑ σε κανένα ΄΄προϊόν’’ αλλά έχει υποβάλει το ερωτηματολόγιο, το πληροφοριακό σύστημα του ΓΕΜΗ θα τον ρωτά αν επιθυμεί να κάνει δήλωση μεταβολής του αρχικής του δήλωσης ΝΑΙ/ΟΧΙ που έχει να κάνει σε σχέση με την διευρυμένη ευθύνη του παραγωγού και την εγγραφή στο Εθνικό Μητρώο Παραγωγών Ε.Μ.ΠΑ ως παρακάτω :"
                    components={{
                      underline: (
                        <span style={{ textDecoration: "underline" }} />
                      ),
                    }}
                  />
                </Typography>

                <Typography sx={{ mb: 2 }}>
                  {t(
                    `-Έχετε δηλώσει σε προηγούμενη είσοδο σας ότι Α) είστε υπόχρεοι σε τουλάχιστον ένα προϊόν από αυτά που υπόκεινται σε Πρόγραμμα Διευρυμένης Ευθύνης Παραγωγού και καλείστε να συνάψετε σύμβαση με Σύστημα/Συστήματα Εναλλακτικής Διαχείρισης (ΣΕΔ) και να εγγραφείτε στο Εθνικό Μητρώο παραγωγών Ε.Μ.ΠΑ.. Εάν επιθυμείτε μπορείτε να κάνετε δήλωση μεταβολήs της αρχικής σας δήλωσης στο ανωτέρω ερωτηματολόγιο.`,
                  )}
                </Typography>

                <Typography sx={{ mb: 2 }}>
                  {t(
                    `-Έχετε δηλώσει σε προηγουμένη είσοδο σας ότι Β) δεν είστε υπόχρεοι σε τουλάχιστον ένα προϊόν από αυτά που υπόκεινται σε Πρόγραμμα Διευρυμένης Ευθύνης Παραγωγού. Εάν επιθυμείτε μπορείτε να κάνετε δήλωση μεταβολή της αρχικής σας δήλωσης στο ανωτέρω ερωτηματολόγιο.`,
                  )}
                </Typography>
              </Box>

              <Box mt={2}>
                <Typography sx={{ mb: 2 }}>
                  {t(
                    `(Η πληροφορία αυτή αποθηκεύεται στο πληροφοριακό του ΓΕΜΗ και του ΕΜΠΑ με το πλήρες ιστορικό).`,
                  )}
                </Typography>

                <Typography>
                  <Trans
                    i18nKey="Περισσότερες πληροφορίες για τα Συστήματα Εναλλακτικής Διαχείρισης (ΣΕΔ) συσκευασιών και άλλων προϊόντων και το Εθνικό Μητρώο Παραγωγών (Ε.Μ.ΠΑ) μπορείτε να δείτε στην ιστοσελίδα του ΕΟΑΝ : https://www.eoan.gr/εμπα/ποιούς-αφορά/ , https://www.eoan.gr/εμπα/συχνές-ερωτήσεις/ , https://www.eoan.gr/εμπα/ενημερωτικό-για-επιχειρήσεις/ ή να επικοινωνήσετε στα τηλέφωνα: 210 866 1695 ( εσ. 223,211,210) ή να στείλετε E-mail: στο register@eoan.gr."
                    components={{
                      link1: (
                        <a
                          href="https://www.eoan.gr/εμπα/ποιούς-αφορά/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "none",
                            color: "#1976d2",
                            fontWeight: "bold",
                          }}
                        />
                      ),
                      link2: (
                        <a
                          href="https://www.eoan.gr/εμπα/συχνές-ερωτήσεις/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "none",
                            color: "#1976d2",
                            fontWeight: "bold",
                          }}
                        />
                      ),
                      link3: (
                        <a
                          href="https://www.eoan.gr/εμπα/ενημερωτικό-για-επιχειρήσεις/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "none",
                            color: "#1976d2",
                            fontWeight: "bold",
                          }}
                        />
                      ),
                    }}
                  />
                </Typography>
              </Box>
              <Box mt={3}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  loading={isLoadingSubmit}
                >
                  {t("Υποβολή")}
                </LoadingButton>
              </Box>
            </>
          </Box>
        </FormContainer>
      )}
    </>
  );
}

export default EmpaForm;
